import {
  useCallback,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";
import { Outlet, useLocation, useNavigate } from "react-router";

import { useAppSelector } from "@/app/hooks";
import { selectAuthActor } from "@/features/auth/authSessionSlice";
import type { PendingWorkspaceSend } from "@/features/chat/WorkspaceConversation";
import {
  firstConversationShopCandidate,
  resolveConversationShopBindingId,
  workspaceConversationPath,
} from "@/features/chat/conversationShopScope";
import { clearPendingHomeIntent } from "@/features/chat/pendingHomeIntent";
import { MerchantHomeRail } from "@/features/merchant-home/MerchantHomeRail";
import { useWorkspaceChatActivity } from "@/features/chat/useWorkspaceChatActivity";
import {
  closeMyFilesDrawerSearch,
  closeStoreOverviewDrawerSearch,
  desktopShellRoutes,
  getDesktopShellRouteByPath,
  isMyFilesDrawerSearch,
  openMyFilesDrawerSearch,
  openStoreOverviewDrawerSearch,
  parseStoreOverviewDrawerSearch,
  updateStoreOverviewDrawerSearch,
} from "@/features/desktop-shell/desktopShellData";
import { DesktopTopNavigation } from "@/features/desktop-shell/DesktopTopNavigation";
import { merryCardMenuCatalog } from "@/features/foundation/cardMenuCatalog";
import { MyFilesView } from "@/features/my-files/MyFilesView";
import {
  StoreOverviewDrawer,
  type StoreOverviewDrawerHandle,
} from "@/features/store-overview/StoreOverviewDrawer";
import { selectWorkspaceStoreSeedBindingId } from "@/features/settings/activeStoreSlice";
import { useGetMerchantStoreBindingsQuery } from "@/features/settings/storeManagementApi";
import { MerryDesktopShell } from "@/shared/ui/MerryDesktopShell";
import { MerryLeftNavigation } from "@/shared/ui/MerryLeftNavigation";
import {
  MerrySettingsNavigation,
  MerrySettingsTitle,
} from "@/shared/ui/MerrySettingsNavigation";

const settingsPageLabels: Readonly<Record<string, string>> = {
  "/settings/account": "账号信息",
  "/settings/ads": "广告户管理",
  "/settings/billing": "套餐与充值",
  "/settings/connections": "应用连接",
  "/settings/notifications": "通知管理",
  "/settings/stores": "店铺管理",
  "/settings/support": "帮助与支持",
  "/settings/team": "团队管理",
  "/settings/tasks": "定时任务管理",
};

export interface RootLayoutOutletContext {
  pendingWorkspaceSend: PendingWorkspaceSend | null;
  setPendingWorkspaceSend: Dispatch<
    SetStateAction<PendingWorkspaceSend | null>
  >;
}

export function RootLayout() {
  const storeOverviewDrawerRef = useRef<StoreOverviewDrawerHandle>(null);
  const [pendingWorkspaceSend, setPendingWorkspaceSend] =
    useState<PendingWorkspaceSend | null>(null);
  const actor = useAppSelector(selectAuthActor);
  const location = useLocation();
  const navigate = useNavigate();
  const organizationId = actor?.activeOrganizationId ?? "";
  const selectedBindingId = useAppSelector((state) =>
    selectWorkspaceStoreSeedBindingId(state, organizationId),
  );
  const storeBindings = useGetMerchantStoreBindingsQuery(organizationId, {
    skip: !organizationId,
  });
  const seedShopBindingId = storeBindings.currentData
    ? resolveConversationShopBindingId(
        selectedBindingId,
        storeBindings.currentData.bindings,
      )
    : "";
  const routeEntry = getDesktopShellRouteByPath(location.pathname);
  const isCreativeMediaCanvas = /\/workspace\/conversations\/[^/]+\/creative-media\/[^/]+$/u.test(
    location.pathname,
  );
  const isSettings = location.pathname.startsWith("/settings/");
  const isWorkspace =
    routeEntry?.[1].mode === "workspace" && !isCreativeMediaCanvas;
  const stateConversationId = location.state && typeof location.state === "object" && "conversationId" in location.state ? location.state.conversationId : "";
  const routeConversationId = /^\/workspace\/conversations\/([^/]+)(?:\/|$)/u.exec(location.pathname)?.[1];
  const conversationId = routeConversationId ? decodeURIComponent(routeConversationId) : typeof stateConversationId === "string" ? stateConversationId.trim() : "";
  const isMerchantHome = location.pathname === "/workspace" && !(typeof stateConversationId === "string" && stateConversationId.trim());
  const isMyFilesOpen =
    isWorkspace && isMyFilesDrawerSearch(location.search);
  const storeOverviewSearch = parseStoreOverviewDrawerSearch(location.search);
  const isStoreOverviewOpen = isWorkspace && storeOverviewSearch.isOpen;
  const routeDefinition = routeEntry?.[1];
  const activeItem = isStoreOverviewOpen
    ? "dataOverview"
    : isMyFilesOpen
    ? "myFiles"
    : (routeDefinition?.activeNavigationItem ?? (routeEntry && routeEntry[0] in desktopShellRoutes ? routeEntry[0] as keyof typeof desktopShellRoutes : "home"));
  const afterStoreOverviewClosed = useCallback(
    (action: () => void) => {
      if (isStoreOverviewOpen && storeOverviewDrawerRef.current) {
        storeOverviewDrawerRef.current.close(action);
        return;
      }
      action();
    },
    [isStoreOverviewOpen],
  );
  const openConversation = useCallback(
    (conversationId: string, shopBindingId?: string | null) => {
      afterStoreOverviewClosed(() =>
        navigate(
          workspaceConversationPath(
            conversationId,
            firstConversationShopCandidate(shopBindingId, seedShopBindingId),
          ),
        ),
      );
    },
    [afterStoreOverviewClosed, navigate, seedShopBindingId],
  );
  const openNewChat = useCallback(() => {
    clearPendingHomeIntent();
    setPendingWorkspaceSend(null);
    afterStoreOverviewClosed(() =>
      navigate(desktopShellRoutes.home.path, { state: null }),
    );
  }, [afterStoreOverviewClosed, navigate]);
  const chatActivity = useWorkspaceChatActivity({
    catalog: merryCardMenuCatalog,
    onNavigateNotification: navigate,
    onNewChat: openNewChat,
    onOpenConversation: openConversation,
  });
  const closeMyFiles = useCallback(() => {
    navigate(
      {
        pathname: desktopShellRoutes.home.path,
        search: closeMyFilesDrawerSearch(location.search),
      },
      { replace: true, state: location.state },
    );
  }, [location.search, location.state, navigate]);
  const closeStoreOverview = useCallback(() => {
    navigate(
      {
        pathname: desktopShellRoutes.home.path,
        search: closeStoreOverviewDrawerSearch(location.search),
      },
      { replace: true, state: location.state },
    );
    window.requestAnimationFrame(() => {
      document
        .querySelector<HTMLElement>(
          '[data-testid="desktop-shell-navigation-item-dataOverview"]',
        )
        ?.focus({ preventScroll: true });
    });
  }, [location.search, location.state, navigate]);
  const updateStoreOverviewSearch = useCallback(
    (update: Parameters<typeof updateStoreOverviewDrawerSearch>[1]) => {
      navigate(
        {
          pathname: desktopShellRoutes.home.path,
          search: updateStoreOverviewDrawerSearch(location.search, update),
        },
        { replace: true, state: location.state },
      );
    },
    [location.search, location.state, navigate],
  );
  const navigateFromLeftNavigation = useCallback(
    (item: keyof typeof desktopShellRoutes) => {
      if (isStoreOverviewOpen && item !== "dataOverview") {
        afterStoreOverviewClosed(() => {
          if (item === "myFiles") {
            navigate({
              pathname: desktopShellRoutes.home.path,
              search: openMyFilesDrawerSearch(""),
            });
            return;
          }
          navigate(desktopShellRoutes[item].path);
        });
        return;
      }

      if (item === "myFiles") {
        navigate(
          {
            pathname: desktopShellRoutes.home.path,
            search: openMyFilesDrawerSearch(
              location.pathname === desktopShellRoutes.home.path
                ? location.search
                : "",
            ),
          },
          {
            state:
              location.pathname === desktopShellRoutes.home.path
                ? location.state
                : undefined,
          },
        );
        return;
      }

      if (item === "dataOverview") {
        navigate(
          {
            pathname: desktopShellRoutes.home.path,
            search: openStoreOverviewDrawerSearch(
              location.pathname === desktopShellRoutes.home.path
                ? closeMyFilesDrawerSearch(location.search)
                : "",
            ),
          },
          {
            state:
              location.pathname === desktopShellRoutes.home.path
                ? location.state
                : undefined,
          },
        );
        return;
      }

      if (
        item === "home" &&
        location.pathname === desktopShellRoutes.home.path
      ) {
        navigate(
          {
            pathname: desktopShellRoutes.home.path,
            search: closeMyFilesDrawerSearch(location.search),
          },
          { state: location.state },
        );
        return;
      }

      navigate(desktopShellRoutes[item].path);
    },
    [
      afterStoreOverviewClosed,
      isStoreOverviewOpen,
      location.pathname,
      location.search,
      location.state,
      navigate,
    ],
  );

  return (
    <MerryDesktopShell
      className={isMerchantHome ? "merry-desktop-shell--merchant-home" : isWorkspace ? "merry-desktop-shell--canonical-rail" : undefined}
      leftNavigation={
        <MerryLeftNavigation
          activeItem={activeItem}
          chatHistoryDrawer={chatActivity.chatHistoryDrawer}
          chatHistoryIndicator={chatActivity.chatHistoryIndicator}
          notificationIndicator={chatActivity.notificationIndicator}
          itemTestIdPrefix="desktop-shell-navigation-item"
          onNavigate={navigateFromLeftNavigation}
          onNewChat={openNewChat}
        />
      }
      mainLabel={
        isSettings
          ? (settingsPageLabels[location.pathname] ?? "设置面板")
          : (routeDefinition?.title ?? "页面内容")
      }
      mode={isCreativeMediaCanvas ? "canvas" : isWorkspace ? "workspace" : "content"}
      overlay={isWorkspace && !isMerchantHome ? chatActivity.notificationOverlay : undefined}
      sideDrawer={
        isStoreOverviewOpen && actor ? (
          <StoreOverviewDrawer
            onClose={closeStoreOverview}
            onSearchChange={updateStoreOverviewSearch}
            organizationId={actor.activeOrganizationId}
            ref={storeOverviewDrawerRef}
            search={storeOverviewSearch}
            userId={actor.userId}
          />
        ) : isMyFilesOpen ? (
          <MyFilesView onClose={closeMyFiles} />
        ) : undefined
      }
      sideDrawerLabel={isStoreOverviewOpen ? "全店数据总览" : "我的文件"}
      sideDrawerVariant={
        isStoreOverviewOpen
          ? storeOverviewSearch.temporaryConversationId &&
            storeOverviewSearch.chatView === "open"
            ? "store-overview-with-chat"
            : "store-overview"
          : "default"
      }
      localNavigation={isSettings ? <MerrySettingsNavigation /> : undefined}
      rightRail={
        isWorkspace ? <MerchantHomeRail key={conversationId || "home"} conversationId={conversationId} /> : undefined
      }
      testId="desktop-shell-root"
      title={
        isCreativeMediaCanvas ? undefined : isSettings ? (
          <MerrySettingsTitle onBack={() => navigate("/workspace")} />
        ) : routeDefinition && !isWorkspace && routeDefinition.titleMode !== "page" ? (
          <h1 className="merry-desktop-shell-page-title">
            {routeDefinition.title}
          </h1>
        ) : undefined
      }
      topNavigation={
        <DesktopTopNavigation beforeNavigate={afterStoreOverviewClosed} />
      }
    >
      <Outlet
        context={{
          pendingWorkspaceSend,
          setPendingWorkspaceSend,
        } satisfies RootLayoutOutletContext}
      />
    </MerryDesktopShell>
  );
}
